import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, Copy, Check, X, Download, Loader2 } from 'lucide-react';
import { useLang } from './i18n';
import { api } from './api';

const trunc = (s, n = 44) => (s && s.length > n ? `${s.slice(0, n)}…` : s || '—');

async function sha256Hex(buf) {
  const d = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(d)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

const Copyable = ({ label, value, testid }) => {
  const { t } = useLang();
  return (
    <div className="nt-kv" style={{ alignItems: 'flex-start' }}>
      <span>{label}</span>
      <span style={{ display: 'flex', gap: 8, alignItems: 'center', maxWidth: '72%' }}>
        <span className="nt-mono" style={{ fontSize: 10, wordBreak: 'break-all' }} data-testid={testid}>{trunc(value)}</span>
        <button className="nt-icon-btn" onClick={() => navigator.clipboard.writeText(value).then(() => toast.success(t('verify.copied')))} title={t('verify.copy')}>
          <Copy size={13} strokeWidth={1.5} />
        </button>
      </span>
    </div>
  );
};

export const VerificadorIndependiente = ({ proof, id }) => {
  const { t } = useLang();
  const [hashState, setHashState] = useState('checking');

  useEffect(() => {
    if (window.location.hash === '#verificador') {
      setTimeout(() => document.getElementById('verificador')?.scrollIntoView({ behavior: 'smooth' }), 350);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(api.proofJsonUrl(id));
        const buf = await res.arrayBuffer();
        const h = await sha256Hex(buf);
        if (alive) setHashState(h === proof.proof_hash ? 'ok' : 'fail');
      } catch {
        if (alive) setHashState('fail');
      }
    })();
    return () => { alive = false; };
  }, [id, proof.proof_hash]);

  // Los snippets se interpolan dentro de un heredoc de shell y de codigo Python: TODO lo que
  // venga del servidor (id, claves, firmas) se valida contra un alfabeto y una longitud exactos
  // antes de entrar en ellos. Con un servidor comprometido, un valor manipulado seria codigo
  // ejecutable en la maquina de quien copia y pega. Si algo no cuadra, no se genera el snippet.
  const SAFE_ID = /^[0-9a-f]{20}$/.test(String(id)) ? id : null;
  const fileBase = SAFE_ID ? `x39-prueba-${SAFE_ID}.json` : 'x39-prueba.json';
  const B64_LEN = (bytes) => 4 * Math.ceil(bytes / 3);           // pk 2592 B -> 3456, sig 4627 B -> 6172
  const safeB64 = (s, bytes) =>
    (typeof s === 'string' && s.length === B64_LEN(bytes) && /^[A-Za-z0-9+/]+={0,2}$/.test(s)) ? s : null;
  // Huella de la clave COLD soberana de X-39. Contrastala fuera de banda (esta web NO
  // basta: un bundle falso puede traer su propia huella coherente consigo misma).
  const COLD_FP = '8453a25a41d6fe8fcb5647600f042a7c303daaca79b80928534025711981c6a1';
  const pySnippet = (pkRaw, sigRaw, tier) => {
    const pk = safeB64(pkRaw, 2592), sig = safeB64(sigRaw, 4627);
    if (!pk || !sig) return `# ${tier}: clave o firma con formato invalido; no se genera el comando. / invalid key or signature format; command not generated.`;
    return `pip install pqcrypto
python3 - <<'EOF'
import base64, hashlib
from pqcrypto.sign import ml_dsa_87 as m
payload = open("${fileBase}", "rb").read()
pk  = base64.b64decode("${pk}")
sig = base64.b64decode("${sig}")

def verify_ok(pk, msg, s):
    # pqcrypto >= 1.0.0 devuelve None si la firma es valida y LANZA si no;
    # versiones previas devuelven True/False. Cubre ambas convenciones.
    try:
        return m.verify(pk, msg, s) is not False
    except Exception:
        return False

valid = verify_ok(pk, payload, sig)
bad = bytearray(sig); bad[0] ^= 1        # control negativo: una firma alterada DEBE fallar
if valid and verify_ok(pk, payload, bytes(bad)):
    print("AVISO: esta instalacion acepta una firma ALTERADA; veredicto no fiable"); valid = False
fp = hashlib.sha256(pk).hexdigest()
print("${tier} ML-DSA-87:", "VALID" if valid else "INVALID", "| fp:", fp)
${tier === 'COLD'
  ? `print("clave COLD", "RECONOCIDA de X-39" if fp == "${COLD_FP}" else "NO RECONOCIDA: NO es la autoridad de X-39")`
  : `print("WARM informativa: clave de servidor retirada 2026-07-16 (SEC-003), no acredita autoria")`}
EOF`;
  };
  const otsCmd = `pip install opentimestamps-client
# guarda ambos archivos con el mismo nombre base:
#   ${fileBase}   y   ${fileBase}.ots
ots verify ${fileBase}.ots
# verificacion soberana (cero terceros) contra TU propio nodo Bitcoin:
# ots --bitcoin-node "http://$(cat ~/.bitcoin/.cookie)@127.0.0.1:8332" verify ${fileBase}.ots`;

  const CmdBlock = ({ title, cmd }) => (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="nt-label">{title}</span>
        <button className="nt-icon-btn" onClick={() => navigator.clipboard.writeText(cmd).then(() => toast.success(t('verify.copied')))} title={t('verify.copy')}>
          <Copy size={13} strokeWidth={1.5} />
        </button>
      </div>
      <pre className="nt-code" data-testid="verify-cmd">{cmd}</pre>
    </div>
  );

  return (
    <div id="verificador" className="nt-card nt-card-pad" style={{ marginTop: 26 }} data-testid="independent-verifier">
      <div className="nt-label" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
        <ShieldCheck size={15} strokeWidth={1.5} /> {t('verify.title')}
      </div>
      <p className="nt-note" style={{ marginTop: 6 }}>{t('verify.intro')}</p>

      <div className="nt-verify-line" data-testid="verify-hash-check" style={{ marginTop: 16 }}>
        {hashState === 'checking' && <Loader2 size={16} className="nt-spin" />}
        {hashState === 'ok' && <Check size={16} color="var(--seal)" />}
        {hashState === 'fail' && <X size={16} color="#c0392b" />}
        <span>{hashState === 'ok' ? t('verify.hashOk') : hashState === 'checking' ? t('verify.checking') : t('verify.hashFail')}</span>
      </div>

      <div className="nt-actions" style={{ marginTop: 14 }}>
        <a className="nt-btn nt-btn-ghost" href={api.proofJsonUrl(id)} data-testid="verify-dl-json"><Download size={13} strokeWidth={1.5} /> {t('verify.dlProof')}</a>
        <a className="nt-btn nt-btn-ghost" href={api.otsUrl(id)} data-testid="verify-dl-ots"><Download size={13} strokeWidth={1.5} /> {t('verify.dlOts')}</a>
      </div>

      {proof.pq && (
        <div style={{ marginTop: 18 }} data-testid="verify-warm">
          <div className="nt-label">{t('verify.warm')}</div>
          <Copyable label={t('verify.pubkey')} value={proof.pq.public_key_b64} testid="verify-warm-pubkey" />
          <Copyable label={t('verify.signature')} value={proof.pq.signature_b64} testid="verify-warm-sig" />
          <CmdBlock title={t('verify.runPy')} cmd={pySnippet(proof.pq.public_key_b64, proof.pq.signature_b64, 'WARM')} />
        </div>
      )}

      <div style={{ marginTop: 18 }} data-testid="verify-cold">
        <div className="nt-label">{t('verify.cold')}</div>
        {proof.cold ? (
          <>
            <Copyable label={t('verify.fp')} value={proof.cold.fingerprint} testid="verify-cold-fp" />
            <p className="nt-note" style={{ marginTop: 4, fontSize: 12 }} data-testid="verify-cold-expected">
              Huella COLD esperada / expected: <span className="nt-mono" style={{ wordBreak: 'break-all' }}>{COLD_FP}</span>.
              Contrástala fuera de banda; deben coincidir. / Cross-check out-of-band; they must match.
            </p>
            <Copyable label={t('verify.pubkey')} value={proof.cold.public_key_b64} testid="verify-cold-pubkey" />
            <Copyable label={t('verify.signature')} value={proof.cold.signature_b64} testid="verify-cold-sig" />
            <CmdBlock title={t('verify.runPy')} cmd={pySnippet(proof.cold.public_key_b64, proof.cold.signature_b64, 'COLD')} />
            <a className="nt-btn nt-btn-ghost" style={{ marginTop: 10 }} href="/runbook-ceremonia-cold.pdf"
              target="_blank" rel="noopener noreferrer" data-testid="verify-cold-runbook">
              <Download size={13} strokeWidth={1.5} /> {t('verify.runbook')}
            </a>
          </>
        ) : (
          <p className="nt-note" style={{ marginTop: 4 }} data-testid="verify-no-cold">{t('verify.noCold')}</p>
        )}
      </div>

      <div style={{ marginTop: 18 }}>
        <div className="nt-label">{t('verify.btc')}</div>
        <CmdBlock title={t('verify.runOts')} cmd={otsCmd} />
        <p className="nt-note" style={{ marginTop: 10, fontSize: 13 }} data-testid="verify-sovereign-note">
          {t('verify.sovereign')}{' '}
          <a href="/success_soberano.png" target="_blank" rel="noopener noreferrer"
            style={{ color: 'var(--seal)' }} data-testid="verify-sovereign-proof">
            {t('verify.sovereignProof')}
          </a>
        </p>
      </div>
    </div>
  );
};
